import { FilterOperations } from 'business/definitions';
import { isNaN, join, map } from 'lodash';

const translateLowerOrGreaterDateFilterValue = (
    t: TranslationT,
    operation: FilterOperationsT | DynamicFilterOperationsT,
    value: string,
): string => {
    const translatedOperation = t(`dateOperations.${operation}`);
    return `${translatedOperation} ${value}`;
};

const translateBetweenDateFilterValue = (
    t: TranslationT,
    operation: FilterOperationsT | DynamicFilterOperationsT,
    value: string,
): string => {
    const translatedOperation = t(`dateOperations.${operation}`);
    const [lowerBound, upperBound] = value.split('|');

    return `${translatedOperation} ${lowerBound} ${t(
        'filterLinker.andThe',
    )} ${upperBound}`;
};

const translateNullDateFilterValue = (
    t: TranslationT,
    operation: FilterOperationsT | DynamicFilterOperationsT,
): string =>
    operation === FilterOperations.equal
        ? t('sentence:is_null')
        : t('sentence:is_not_null');

const translateCurrentDateFilterValue = (
    t: TranslationT,
    operation: FilterOperationsT | DynamicFilterOperationsT,
    valueScale: string,
): string => t(`${operation}_${valueScale}`);

const translateLastDateFilterValue = (
    t: TranslationT,
    operation: FilterOperationsT | DynamicFilterOperationsT,
    value: string,
    valueScale: string,
): string =>
    `${t('filterLinker.forThe')} ${value} ${t(operation)} ${t(valueScale)}`;

export const translateYearFilterValue = (
    t: TranslationT,
    filter: SentenceFilterT,
): string => {
    const operation = t(`yearOperations.${filter.operation}`);
    const valueParsed = filter.value ? parseFloat(filter.value) : NaN;
    const value = isNaN(valueParsed)
        ? t(`yearOperations.${filter.value}`)
        : filter.value;
    return `${operation} ${value}`;
};

const translateExtractionFilter = (
    t: TranslationT,
    filter: SentenceFilterT,
): string => {
    const operation = t(`dateOperations.${filter.operation}`);
    const values = join(
        map(filter.values, (v) => t(`extract.${filter.valueScale}.${v.value}`)),
        ', ',
    );
    const scale = t(`${filter.valueScale}`);
    const link1 = t('filterLinker.the');
    const link2 = t('filterLinker.in');
    return `${operation} ${link1} ${scale} ${link2} ${values}`;
};

export const translateDateFilterValue = (
    t: TranslationT,
    filter: SentenceFilterT,
): string => {
    const { operation, value, valueScale, type } = filter;

    if (type === 'year') {
        return translateYearFilterValue(t, filter);
    }

    if (type === 'date' && operation === 'extract') {
        return translateExtractionFilter(t, filter);
    }

    if (
        value === '' &&
        [FilterOperations.equal, FilterOperations.not_equal].includes(operation)
    ) {
        return translateNullDateFilterValue(t, operation);
    }
    if (!value) {
        throw new Error('PrintDateTranslationError, value should be defined');
    }

    if (
        [
            FilterOperations.greater,
            FilterOperations.lower,
            FilterOperations.greater_or_equal,
            FilterOperations.lower_or_equal,
        ].includes(operation)
    ) {
        return translateLowerOrGreaterDateFilterValue(t, operation, value);
    }
    if (operation === FilterOperations.between) {
        return translateBetweenDateFilterValue(t, operation, value);
    }

    if (!valueScale) {
        throw new Error(
            `PrintDateTranslationError, valueScale should be defined for operation ${operation}`,
        );
    }

    if (value === '1') {
        return translateCurrentDateFilterValue(t, operation, valueScale);
    }

    return translateLastDateFilterValue(t, operation, value, valueScale);
};
